// src/pages/dashboard/branch/categories/CategoriesPage.tsx
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiEdit,
  FiEye,
  FiEyeOff,
  FiGrid,
  FiLayers,
  FiMenu,
  FiMove,
  FiPlus,
  FiSearch,
  FiSettings,
  FiTrash2,
} from "react-icons/fi";
import { Link, useNavigate, useParams } from "react-router-dom";
import Badge from "../../../../components/ui/Badge";
import Button from "../../../../components/ui/Button";
import Card from "../../../../components/ui/Card";
import Skeleton from "../../../../components/ui/Skeleton";
import { CategoryWithSubcategories } from "../../../../types/category";
import {
  getCategoryDescription,
  getCategoryName,
} from "../../../../utils/categoryHelper";
import { Category, updateCategory, batchUpdateCategorySortOrders } from "../../../../utils/supabaseClient";
import AddCategoryModal from "./components/AddCategoryModal";
import DeleteCategoryModal from "./components/DeleteCategoryModal";
import UpdateCategoryModal from "./components/UpdateCategoryModal";

import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  selectCategoriesHierarchy,
  selectCategoryLoading,
} from "../../../../store/selectors";
import { setHasChanges } from '../../../../store/slices/branchSlice';
import { fetchCategories, reorderCategories, toggleCategoryPublishStatus } from "../../../../store/slices/categorySlice";

const CategoriesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";

  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectCategoryLoading);
  const categories = useAppSelector(selectCategoriesHierarchy);

  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(
    null
  );
  const [expandedCategories, setExpandedCategories] = useState<{
    [key: string]: boolean;
  }>({});
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );
  const [togglingCategoryId, setTogglingCategoryId] = useState<string | null>(null);

  const { branchId } = useParams<{ branchId: string }>();

  const isDndActive = viewMode === 'list' && searchQuery.trim() === '';

  useEffect(() => {
    if (branchId) {
      dispatch(fetchCategories({ branchId })).unwrap();
    }
  }, [branchId, dispatch]);

  const handleCreateCategory = (_newCategory: Category) => {
    setIsCreateModalOpen(false);
  };

  const handleDeleteClick = (category: Category) => {
    setCategoryToDelete(category);
    setIsDeleteModalOpen(true);
  };

  const handleManageItems = (categoryId: string) => {
    navigate(`/dashboard/${branchId}/menu?category=${categoryId}`);
  };

  const handleToggleCategoryPublish = async (category: Category) => {
    const updatedData = {
      id: category.id,
      is_published: !category.is_published,
    };

    setTogglingCategoryId(category.id);
    dispatch(toggleCategoryPublishStatus(category.id));

    try {
      await updateCategory(updatedData);
    } catch (error) {
      console.error('Error toggling category publish status:', error);
      dispatch(toggleCategoryPublishStatus(category.id));
    } finally {
      setTogglingCategoryId(null);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || result.source.index === result.destination.index) return;

    const reordered = Array.from(filteredCategories);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    dispatch(reorderCategories({ orderedIds: reordered.map(c => c.id) }));
    await batchUpdateCategorySortOrders(reordered.map((cat, idx) => ({ id: cat.id, sort_order: idx })));
    dispatch(setHasChanges(true));
  };

  const toggleExpanded = (categoryId: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const filteredCategories = categories.filter(
    (category) =>
      getCategoryName(category, i18n.language)
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      (getCategoryDescription(category, i18n.language) &&
        getCategoryDescription(category, i18n.language)!
          .toLowerCase()
          .includes(searchQuery.toLowerCase()))
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  const renderCategoryList = (
    categories: CategoryWithSubcategories[],
    level = 0
  ) => {
    return categories.map((category) => (
      <React.Fragment key={category.id}>
        <tr
          className={`border-b border-gray-200 dark:border-gray-700 ${level > 0 ? "bg-gray-50 dark:bg-gray-800/50" : ""
            }`}
        >
          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
            <div className="flex items-center">
              <div style={{ width: `${level * 24}px` }} />
              {!category.parent_id && category.children?.length ? (
                <button
                  onClick={() => toggleExpanded(category.id)}
                  className="p-1 me-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                >
                  <FiChevronDown
                    className={`h-4 w-4 text-gray-500 transition-transform ${expandedCategories[category.id] ? "rotate-180" : ""
                      }`}
                  />
                </button>
              ) : (
                <span className="me-2"></span>
              )}
              {category.image_url && (
                <img
                  src={category.image_url}
                  alt={getCategoryName(category, i18n.language)}
                  className="h-8 w-8 rounded object-cover me-3"
                />
              )}
              <span>{getCategoryName(category, i18n.language)}</span>
            </div>
          </td>
          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 align-top">
            <div className="max-w-[22rem] whitespace-normal break-words">
              {getCategoryDescription(category, i18n.language) || (
                <span className="text-gray-400 dark:text-gray-500 italic">
                  {t("categories.noDescription")}
                </span>
              )}
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
            {category.is_published ? (
              <Badge variant="success">{t("categories.published")}</Badge>
            ) : (
              <Badge variant="gray">{t("categories.draft")}</Badge>
            )}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-end text-sm font-medium">
            <div className="flex justify-end items-center gap-2">
              {category.parent_id && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleManageItems(category.id)}
                  leftIcon={<FiSettings />}
                >
                  {t("categories.manageItems")}
                </Button>
              )}
              <Link
                to={`/dashboard/${branchId}/categories/${category.id}`}
                className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
              >
                <FiEye className="w-5 h-5" />
                <span className="sr-only">{t("common.view")}</span>
              </Link>
              <button
                onClick={() => {
                  setSelectedCategory(category);
                  setShowCategoryModal(true);
                }}
                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer"
              >
                <FiEdit className="w-5 h-5" />
                <span className="sr-only">{t("common.edit")}</span>
              </button>
              <button
                onClick={() => handleDeleteClick(category)}
                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 cursor-pointer"
              >
                <FiTrash2 className="w-5 h-5" />
                <span className="sr-only">{t("common.delete")}</span>
              </button>
            </div>
          </td>
        </tr>
        {expandedCategories[category.id] &&
          category.children &&
          category.children.length > 0 &&
          renderCategoryList(category.children, level + 1)}
      </React.Fragment>
    ));
  };

  const renderCategoryCard = (category: CategoryWithSubcategories) => (
    <Card className="h-full transition-shadow hover:shadow-md border border-gray-200 dark:border-gray-700 flex flex-col">
      {category.image_url && (
        <div className="aspect-w-16 aspect-h-9 mb-4 ">
          <img
            src={category.image_url}
            alt={getCategoryName(category, i18n.language)}
            className="w-full h-40 object-cover rounded-lg"
          />
        </div>
      )}

      <div className="flex justify-between items-start mb-3">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          {getCategoryName(category, i18n.language)}
        </h3>
        {category.is_published ? (
          <Badge variant="success">
            {t("categories.published")}
          </Badge>
        ) : (
          <Badge variant="gray">{t("categories.draft")}</Badge>
        )}
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-4 break-words">
        {getCategoryDescription(category, i18n.language) || (
          <span className="text-gray-400 dark:text-gray-500 italic">
            {t("categories.noDescription")}
          </span>
        )}
      </p>

      {category.children && category.children.length > 0 ? (
        <div className="mb-4">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
            {t("categories.subcategories")}:
          </p>
          <div className="flex flex-wrap gap-2">
            {category.children.map((child) => (
              <Link
                key={child.id}
                to={`/dashboard/${branchId}/categories/${child.id}`}
                className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                {getCategoryName(child, i18n.language)}
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div className="mb-4">
          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-4 min-h-[7vh] break-words"></p>
        </div>
      )}

      <div className="mt-auto pt-4 border-t border-gray-200 dark:border-gray-700 min-h-[50px]">
        {category.parent_id ? (
          <div className="flex items-center justify-between">
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleManageItems(category.id)
              }}
              leftIcon={<FiSettings />}
            >
              {t("categories.manageItems")}
            </Button>

            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleToggleCategoryPublish(category)
                }}
                disabled={togglingCategoryId === category.id}
                className="text-gray-400 hover:text-blue-500 dark:text-gray-500 dark:hover:text-blue-400 disabled:opacity-50 cursor-pointer transition-colors"
                title={category.is_published ? t('common.unpublish') : t('common.publish')}
              >
                {togglingCategoryId === category.id ? (
                  <div className="animate-spin">
                    <FiEyeOff className="h-5 w-5" />
                  </div>
                ) : category.is_published ? (
                  <FiEye className="h-5 w-5" />
                ) : (
                  <FiEyeOff className="h-5 w-5" />
                )}
                <span className="sr-only">{category.is_published ? t('common.unpublish') : t('common.publish')}</span>
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setSelectedCategory(category);
                  setShowCategoryModal(true);
                }}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 cursor-pointer"
              >
                <FiEdit className="h-5 w-5" />
                <span className="sr-only">{t("common.edit")}</span>
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleDeleteClick(category)
                }}
                className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 cursor-pointer"
              >
                <FiTrash2 className="h-5 w-5" />
                <span className="sr-only">
                  {t("common.delete")}
                </span>
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="text-sm text-primary hover:text-primary-dark flex items-center cursor-pointer"
              onClick={() => navigate(`/dashboard/${branchId}/menu?category=${category.id}`)}
            >
              {t("categories.viewItems")}
              {isRTL ? (
                <FiChevronLeft className="ms-1 h-4 w-4" />
              ) : (
                <FiChevronRight className="ms-1 h-4 w-4" />
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleToggleCategoryPublish(category)
                }}
                disabled={togglingCategoryId === category.id}
                className="text-gray-400 hover:text-blue-500 dark:text-gray-500 dark:hover:text-blue-400 disabled:opacity-50 cursor-pointer transition-colors"
                title={category.is_published ? t('common.unpublish') : t('common.publish')}
              >
                {togglingCategoryId === category.id ? (
                  <div className="animate-spin">
                    <FiEyeOff className="h-5 w-5" />
                  </div>
                ) : category.is_published ? (
                  <FiEye className="h-5 w-5" />
                ) : (
                  <FiEyeOff className="h-5 w-5" />
                )}
                <span className="sr-only">{category.is_published ? t('common.unpublish') : t('common.publish')}</span>
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setSelectedCategory(category);
                  setShowCategoryModal(true);
                }}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 cursor-pointer"
              >
                <FiEdit className="h-5 w-5" />
                <span className="sr-only">{t("common.edit")}</span>
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleDeleteClick(category)
                }}
                className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 cursor-pointer"
              >
                <FiTrash2 className="h-5 w-5" />
                <span className="sr-only">
                  {t("common.delete")}
                </span>
              </button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap mb-6">
        <div className="">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {t("categories.title")}
          </h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            {t("categories.subtitle")}
          </p>
        </div>
        <Button
          className="mt-4 sm:mt-0 w-full sm:w-fit"
          onClick={() => setIsCreateModalOpen(true)}
          leftIcon={<FiPlus />}
        >
          {t("categories.addCategory")}
        </Button>
      </div>

      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        className="fixed bottom-16 end-6 sm:hidden z-50"
      >
        <Button
          size="lg"
          onClick={() => setIsCreateModalOpen(true)}
          className="!rounded-full !w-14 !h-14 !p-0 shadow-lg"
        >
          <FiPlus className="w-6 h-6" />
        </Button>
      </motion.div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
          <div className="relative basis-2/3 max-w-86">
            <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
              <FiSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full ps-10 pe-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              placeholder={t("common.search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery.trim().length > 0 && (
              <p className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 mt-1.5">
                <FiMove className="w-3 h-3 flex-shrink-0" />
                {t('settings.searchActiveNoDnd')}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded-md cursor-pointer ${viewMode === "grid"
                ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                : "text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
            >
              <FiGrid className="h-5 w-5" />
              <span className="sr-only">{t("categories.gridView")}</span>
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded-md cursor-pointer ${viewMode === "list"
                ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                : "text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
            >
              <FiMenu className="h-5 w-5" />
              <span className="sr-only">{t("categories.listView")}</span>
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="p-6">
            {viewMode === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div
                    key={i}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-6"
                  >
                    <Skeleton height="h-40" width="w-full" className="mb-4" />
                    <Skeleton height="h-6" width="w-3/4" className="mb-4" />
                    <Skeleton height="h-4" width="w-full" className="mb-3" />
                    <Skeleton height="h-4" width="w-5/6" className="mb-6" />
                    <div className="flex justify-between items-center">
                      <Skeleton
                        height="h-8"
                        width="w-16"
                        rounded="rounded-md"
                      />
                      <div className="flex gap-2">
                        <Skeleton
                          height="h-8"
                          width="w-8"
                          rounded="rounded-full"
                        />
                        <Skeleton
                          height="h-8"
                          width="w-8"
                          rounded="rounded-full"
                        />
                        <Skeleton
                          height="h-8"
                          width="w-8"
                          rounded="rounded-full"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border">
                <div className="bg-gray-50 dark:bg-gray-800">
                  <div className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-full">
                    <Skeleton height="h-4" width="w-full" />
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="px-6 py-4">
                      <Skeleton height="h-6" width="w-full" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="text-center py-12">
            <FiLayers className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              {t("categories.noCategories")}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t("categories.noCategoriesTooltip")}
            </p>
            <div className="mt-6">
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                leftIcon={<FiPlus />}
              >
                {t("categories.createFirstCategory")}
              </Button>
            </div>
          </div>
        ) : viewMode === "grid" ? (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 "
          >
            {filteredCategories.map((category) => (
              <motion.div
                key={category.id}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
              >
                <div
                  className="h-full cursor-pointer"
                  onClick={() => navigate(`/dashboard/${branchId}/categories/${category.id}`)}
                >
                  {renderCategoryCard(category)}
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          // List view
          <div className="overflow-x-auto ">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {isDndActive && (
                    <th scope="col" className="px-3 py-3 w-10" />
                  )}
                  <th
                    scope="col"
                    className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    {t("categories.name")}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    {t("categories.description")}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    {t("categories.status")}
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">{t("common.actions")}</span>
                  </th>
                </tr>
              </thead>
              {isDndActive && (
                <thead className="bg-primary/5 dark:bg-primary/10">
                  <tr>
                    <td colSpan={5} className="px-4 py-2">
                      <span className="flex items-center gap-1.5 text-xs text-primary font-medium">
                        <FiMove className="w-3 h-3" />
                        {t('settings.dragToReorder')}
                      </span>
                    </td>
                  </tr>
                </thead>
              )}
              {isDndActive ? (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="categories-list">
                    {(provided) => (
                      <tbody
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                      >
                        {filteredCategories.map((category, index) => (
                          <Draggable
                            key={category.id}
                            draggableId={category.id}
                            index={index}
                            isDragDisabled={!!searchQuery}
                          >
                            {(provided, snapshot) => (
                              <tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`border-b border-gray-200 dark:border-gray-700 transition-colors ${snapshot.isDragging ? 'bg-primary/5 dark:bg-primary/10 shadow-lg' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}
                              >
                                <td className="px-3 py-4 w-10">
                                  <div
                                    {...provided.dragHandleProps}
                                    className="cursor-grab active:cursor-grabbing text-primary hover:text-primary/80 transition-colors p-1.5 rounded-md bg-primary/10 hover:bg-primary/20"
                                    title={t('settings.dragToReorder')}
                                  >
                                    <FiMove className="w-4 h-4" />
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <div className="flex items-center">
                                    {category.image_url && (
                                      <img
                                        src={category.image_url}
                                        alt={getCategoryName(category, i18n.language)}
                                        className="h-8 w-8 rounded object-cover me-3"
                                      />
                                    )}
                                    <span>{getCategoryName(category, i18n.language)}</span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 align-top">
                                  <div className="max-w-[22rem] whitespace-normal break-words">
                                    {getCategoryDescription(category, i18n.language) || (
                                      <span className="text-gray-400 dark:text-gray-500 italic">
                                        {t("categories.noDescription")}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {category.is_published ? (
                                    <Badge variant="success">{t("categories.published")}</Badge>
                                  ) : (
                                    <Badge variant="gray">{t("categories.draft")}</Badge>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-end text-sm font-medium">
                                  <div className="flex justify-end items-center gap-2">
                                    {category.parent_id && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleManageItems(category.id)}
                                        leftIcon={<FiSettings />}
                                      >
                                        {t("categories.manageItems")}
                                      </Button>
                                    )}
                                    <Link
                                      to={`/dashboard/${branchId}/categories/${category.id}`}
                                      className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                                    >
                                      <FiEye className="w-5 h-5" />
                                    </Link>
                                    <button
                                      onClick={() => {
                                        setSelectedCategory(category);
                                        setShowCategoryModal(true);
                                      }}
                                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer"
                                    >
                                      <FiEdit className="w-5 h-5" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteClick(category)}
                                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 cursor-pointer"
                                    >
                                      <FiTrash2 className="w-5 h-5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </tbody>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {renderCategoryList(filteredCategories)}
                </tbody>
              )}
            </table>
          </div>
        )}
      </div>

      <AddCategoryModal
        onClose={() => setIsCreateModalOpen(false)}
        isOpen={isCreateModalOpen}
        branchId={branchId!}
        mainCategories={categories.filter((cat) => !cat.parent_id)}
        onCategoryAdded={handleCreateCategory}
      />

      <UpdateCategoryModal
        isOpen={showCategoryModal && !!selectedCategory}
        category={selectedCategory!}
        onClose={() => {
          setSelectedCategory(null);
          setShowCategoryModal(false);
        }}
        parentCategories={categories.filter((cat) => !cat.parent_id)}
        onCategoryUpdated={() => {
          if (branchId) {
            dispatch(fetchCategories({ branchId, forceRefresh: true }));
          }
        }}
        branchId={branchId!}
      />

      <DeleteCategoryModal
        isOpen={isDeleteModalOpen && !!categoryToDelete}
        category={categoryToDelete!}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setCategoryToDelete(null);
        }}
        onCategoryDeleted={(_deletedCategory) => {
          setIsDeleteModalOpen(false);
          setCategoryToDelete(null);
        }}
      />
    </div>
  );
};

export default CategoriesPage;
